// src/services/stripe.service.js
import { loadStripe } from '@stripe/stripe-js';
import { supabase } from '../utils/supabase';
import { getPriceIdForTier } from '../config/pricing';
import { checkBillingMaintenance } from '../hooks/useAppConfig';

/**
 * Centralized maintenance gate for every Stripe-touching action below.
 *
 * Called at the top of createCheckoutSession, createUpgradeSession,
 * cancelSubscription, reactivateSubscription, and createPortalSession
 * so that no matter which surface (in-app billing, marketing pricing,
 * usage banners, signup flow) triggers a billing change, it gets the
 * same "we're under maintenance" error response instead of opening a
 * Stripe checkout with potentially mismatched price IDs during a
 * mid-deploy window.
 *
 * Returns { error: string } when blocked (the same shape every caller
 * already handles), or null when the action should proceed.
 */
async function guardBillingMaintenance() {
  const { enabled, message } = await checkBillingMaintenance();
  if (enabled) {
    return {
      error: message || 'Billing changes are temporarily under maintenance. Please check back soon.',
    };
  }
  return null;
}

// Lazy initialize Stripe to avoid errors if key is undefined
let stripePromise = null;
const getStripeInstance = () => {
  if (!stripePromise) {
    const key = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
    if (key) {
      stripePromise = loadStripe(key);
    }
  }
  return stripePromise;
};

// Legacy single price ID (backward compat for existing callsites)
const LEGACY_PRICE_ID = process.env.REACT_APP_STRIPE_PRICE_ID;

/**
 * Resolve Stripe price ID and validate inputs.
 * Shared by both checkout (new signup) and upgrade (logged-in) flows.
 */
function resolveCheckoutParams(userId, userEmail, tierId, billingInterval) {
  let priceId;
  if (tierId && tierId !== 'free') {
    priceId = getPriceIdForTier(tierId, billingInterval || 'month');
    if (!priceId) {
      return { error: `The ${tierId} plan is not available yet. Please contact support.` };
    }
  } else {
    priceId = LEGACY_PRICE_ID;
  }

  if (!priceId) {
    return { error: 'Payment system is not configured. Please contact support.' };
  }

  if (!userId || !userEmail) {
    return { error: 'User information is missing. Please try again.' };
  }

  return { priceId };
}

/**
 * Handle the response from a Stripe edge function (checkout or upgrade).
 */
function handleStripeResponse(data, error) {
  if (error) {
    console.error('Edge function error:', error);
    if (error.context) {
      // Try to extract the actual error from the response body
      return error.context.json().then(errBody => {
        console.error('Edge function response body:', errBody);
        return { error: errBody?.error || errBody?.message || 'Payment service error. Please try again.' };
      }).catch(() => {
        return { error: 'Payment service temporarily unavailable. Please try again.' };
      });
    }
    return Promise.resolve({ error: error.message || 'Payment service error. Please try again.' });
  }

  if (data?.error) {
    console.error('Stripe session error:', data.error);
    return Promise.resolve({ error: data.error });
  }

  if (data?.url) {
    // Validate Stripe URL before redirect (prevent open redirect)
    const allowedStripeDomains = ['https://checkout.stripe.com', 'https://billing.stripe.com'];
    const isValidStripeUrl = allowedStripeDomains.some(domain => data.url.startsWith(domain));
    if (!isValidStripeUrl) {
      console.error('Invalid Stripe redirect URL:', data.url);
      return Promise.resolve({ error: 'Invalid checkout URL received. Please try again.' });
    }
    window.location.href = data.url;
  } else if (data?.sessionId) {
    window.location.href = `https://checkout.stripe.com/c/pay/${data.sessionId}`;
  } else {
    console.error('No checkout URL returned:', data);
    return Promise.resolve({ error: 'Failed to get checkout URL. Please try again.' });
  }

  return Promise.resolve({ success: true });
}

/**
 * Create a Stripe Checkout session for NEW SIGNUPS choosing a paid plan.
 * Uses create-checkout-session edge function (deployed with --no-verify-jwt).
 * Auth: validates userId/email against auth.users server-side.
 */
export const createCheckoutSession = async (userId, userEmail, tierId, billingInterval, successUrl, cancelUrl, turnstileToken) => {
  try {
    // Maintenance gate — prevents new subscriptions during a deploy window
    // when env-var price IDs may be mid-rotation.
    const maint = await guardBillingMaintenance();
    if (maint) return maint;

    const params = resolveCheckoutParams(userId, userEmail, tierId, billingInterval);
    if (params.error) return params;

    const rewardfulReferral = (typeof window !== 'undefined' && window.Rewardful?.referral) || '';

    const { data, error } = await supabase.functions.invoke('create-checkout-session', {
      body: {
        userId,
        userEmail,
        priceId: params.priceId,
        tierId: tierId || 'pro',
        billingInterval: billingInterval || 'month',
        successUrl: successUrl || `${window.location.origin}/?checkout=success`,
        cancelUrl: cancelUrl || `${window.location.origin}/?checkout=canceled`,
        ...(rewardfulReferral && { rewardfulReferral }),
        ...(turnstileToken && { turnstileToken }),
      },
    });

    return await handleStripeResponse(data, error);
  } catch (error) {
    console.error('Error creating checkout session:', error);
    return { error: error.message || 'Failed to create checkout session' };
  }
};

/**
 * Upgrade a logged-in user's plan.
 * Uses create-upgrade-session edge function (JWT required by gateway).
 * - If user has an active subscription → updates in-place with proration, charged immediately (no redirect).
 * - If user has no subscription → creates a Checkout session (redirects to Stripe).
 */
export const createUpgradeSession = async (tierId, billingInterval, successUrl, cancelUrl) => {
  try {
    // Maintenance gate (see guardBillingMaintenance above).
    const maint = await guardBillingMaintenance();
    if (maint) return maint;

    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      return { error: 'You must be logged in to upgrade. Please sign in and try again.' };
    }

    const priceId = getPriceIdForTier(tierId, billingInterval || 'month');
    if (!priceId) {
      return { error: `The ${tierId} plan is not available yet. Please contact support.` };
    }

    const { data, error } = await supabase.functions.invoke('create-upgrade-session', {
      body: {
        priceId,
        tierId: tierId || 'pro',
        billingInterval: billingInterval || 'month',
        successUrl: successUrl || `${window.location.origin}/account?checkout=success`,
        cancelUrl: cancelUrl || `${window.location.origin}/account?checkout=canceled`,
      },
    });

    // Handle edge function errors
    if (error) {
      console.error('Upgrade edge function error:', error);
      if (error.context) {
        try {
          const errBody = await error.context.json();
          console.error('Upgrade response body:', errBody);
          return { error: errBody?.error || errBody?.message || 'Upgrade failed. Please try again.' };
        } catch {
          // couldn't parse response
        }
      }
      return { error: error.message || 'Upgrade failed. Please try again.' };
    }

    if (data?.error) {
      return { error: data.error };
    }

    // In-place upgrade completed (no redirect needed)
    if (data?.upgraded) {
      return { success: true, upgraded: true, plan: data.plan, interval: data.interval };
    }

    // New checkout session — redirect to Stripe
    return await handleStripeResponse(data, null);
  } catch (error) {
    console.error('Error creating upgrade session:', error);
    return { error: error.message || 'Failed to create upgrade session' };
  }
};

/**
 * Cancel a logged-in user's subscription at period end.
 * Uses create-upgrade-session edge function with action: "cancel".
 */
export const cancelSubscription = async () => {
  try {
    // Maintenance gate — also blocks cancellation during the window so
    // we don't end up with a race between "user canceled" and "admin
    // migrating plan_type". UI is also gated upstream, but defense in
    // depth at the service layer.
    const maint = await guardBillingMaintenance();
    if (maint) return maint;

    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      return { error: 'You must be logged in to cancel. Please sign in and try again.' };
    }

    const { data, error } = await supabase.functions.invoke('create-upgrade-session', {
      body: { action: 'cancel' },
    });

    if (error) {
      console.error('Cancel edge function error:', error);
      if (error.context) {
        try {
          const errBody = await error.context.json();
          console.error('Cancel response body:', errBody);
          return { error: errBody?.error || errBody?.message || 'Cancellation failed. Please try again.' };
        } catch {
          // couldn't parse response
        }
      }
      return { error: error.message || 'Cancellation failed. Please try again.' };
    }

    if (data?.error) {
      return { error: data.error };
    }

    if (data?.canceled) {
      return { success: true, canceled: true, effectiveDate: data.effectiveDate };
    }

    return { error: 'Unexpected response. Please try again.' };
  } catch (error) {
    console.error('Error canceling subscription:', error);
    return { error: error.message || 'Failed to cancel subscription' };
  }
};

/**
 * Reactivate a cancelled subscription (undo cancel_at_period_end).
 * Uses create-upgrade-session edge function with action: "reactivate".
 */
export const reactivateSubscription = async () => {
  try {
    const maint = await guardBillingMaintenance();
    if (maint) return maint;

    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      return { error: 'You must be logged in to reactivate. Please sign in and try again.' };
    }

    const { data, error } = await supabase.functions.invoke('create-upgrade-session', {
      body: { action: 'reactivate' },
    });

    if (error) {
      console.error('Reactivate edge function error:', error);
      if (error.context) {
        try {
          const errBody = await error.context.json();
          console.error('Reactivate response body:', errBody);
          return { error: errBody?.error || errBody?.message || 'Reactivation failed. Please try again.' };
        } catch {
          // couldn't parse response
        }
      }
      return { error: error.message || 'Reactivation failed. Please try again.' };
    }

    if (data?.error) {
      return { error: data.error };
    }

    if (data?.reactivated) {
      return { success: true, reactivated: true };
    }

    return { error: 'Unexpected response. Please try again.' };
  } catch (error) {
    console.error('Error reactivating subscription:', error);
    return { error: error.message || 'Failed to reactivate subscription' };
  }
};

/**
 * Create a Stripe Customer Portal session
 * Allows users to manage their subscription, update payment method, etc.
 */
export const createPortalSession = async (userId, returnUrl) => {
  try {
    const maint = await guardBillingMaintenance();
    if (maint) return maint;

    const { data, error } = await supabase.functions.invoke('create-portal-session', {
      body: {
        userId,
        returnUrl: returnUrl || `${window.location.origin}/settings`,
      },
    });

    if (error) throw error;

    // Validate Stripe URL before redirect (prevent open redirect)
    if (!data.url || !data.url.startsWith('https://billing.stripe.com')) {
      console.error('Invalid Stripe portal URL:', data.url);
      return { error: 'Invalid portal URL received. Please try again.' };
    }
    window.location.href = data.url;
    return { success: true };
  } catch (error) {
    console.error('Error creating portal session:', error);
    return { error: error.message || 'Failed to create portal session' };
  }
};

/**
 * Get Stripe instance for client-side operations
 */
export const getStripe = () => getStripeInstance();

export default {
  createCheckoutSession,
  createUpgradeSession,
  cancelSubscription,
  reactivateSubscription,
  createPortalSession,
  getStripe,
};
